import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Layouts
import { AuthLayout } from '../components/layouts/AuthLayout';
import { ShopLayout } from '../components/layouts/ShopLayout';
import { UserLayout } from '../components/layouts/UserLayout';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { ErrorLayout } from '../components/layouts/ErrorLayout';

// Context / Hook / Page
import { MainContext } from '../store/contexts';
import { useAppInitializer } from '../hooks/useAppInitializer';
import { LoadingPage } from '../components/common/page/LoadingPage';

// Shop pages
import { HomePage } from '../pages/shop/HomePage';
import { CartPage } from '../pages/shop/CartPage';
import { SearchPage } from '../pages/shop/SearchPage';

// Auth pages
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';

// User pages
import { UserOrdersPage } from '../pages/user/UserOrdersPage';
import { UserProfilePage } from '../pages/user/UserProfilePage';

// Admin pages
import { AdminDashboardPage } from '../pages/admin/AdminDashboardPage';
import { AdminCategoriesPage } from '../pages/admin/AdminCategoriesPage';
import { AdminProductsPage } from '../pages/admin/AdminProductsPage';
import { AdminUsersPage } from '../pages/admin/AdminUsersPage';
import { AdminOrdersPage } from '../pages/admin/AdminOrdersPage';
import { CreateOrderPage } from '../pages/orders/CreateOrderPage';
import { EditOrderPage } from '../pages/orders/EditOrderPage';

// Error pages
import { ForbiddenPage } from '../pages/error/ForbiddenPage';
import { NotFoundPage } from '../pages/error/NotFoundPage';
import { PageExpiredPage } from '../pages/error/PageExpiredPage';
import { ServerErrorPage } from '../pages/error/ServerErrorPage';
import { ServiceUnavailablePage } from '../pages/error/ServiceUnavailablePage';
import { TooManyRequestsPage } from '../pages/error/TooManyRequestsPage';
import { UnauthorizedPage } from '../pages/error/UnauthorizedPage';
import { NotificationsPage } from '../pages/shop/NotificationsPage';

export const AppRouter = () => {
    const { isLoading, hasException, exception, data } = useAppInitializer();
    console.log('LOADING');

    if (isLoading) {
        console.log('NO RENDER');
        return <LoadingPage />;
    }

    if (hasException) {
        console.log('HAS EXCEPTION');
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="*" element={<ErrorLayout />}>
                        <Route path="*" element={<ServerErrorPage exception={exception} />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        );
    }

    console.log('RENDER');

    return (
        <MainContext.Provider value={{ ...data }}>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<ShopLayout />}>
                        <Route index element={<HomePage />} />
                        <Route exact path="cart" element={<CartPage />} />
                        <Route exact path="search" element={<SearchPage />} />
                        <Route exact path="notifications" element={<NotificationsPage />} />
                        <Route path="/user" element={<UserLayout />}>
                            <Route index element={<UserProfilePage />} />
                            <Route exact path="orders" element={<UserOrdersPage />} />
                        </Route>
                    </Route>
                    <Route path="/" element={<AuthLayout />}>
                        <Route exact path="/login" element={<LoginPage />} />
                        <Route exact path="/register" element={<RegisterPage />} />
                    </Route>
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboardPage />} />
                        <Route exact path="categories" element={<AdminCategoriesPage />} />
                        <Route exact path="products" element={<AdminProductsPage />} />
                        <Route exact path="users" element={<AdminUsersPage />} />
                        <Route exact path="orders" element={<AdminOrdersPage />} />
                        <Route exact path="orders/create" element={<CreateOrderPage />} />
                        <Route exact path="orders/edit/:id" element={<EditOrderPage />} />
                    </Route>
                    <Route path="/" element={<ErrorLayout />}>
                        <Route exact path="unauthorized" element={<UnauthorizedPage />} />
                        <Route exact path="forbidden" element={<ForbiddenPage />} />
                        <Route exact path="not-found" element={<NotFoundPage />} />
                        <Route exact path="page-expired" element={<PageExpiredPage />} />
                        <Route exact path="too-many-requests" element={<TooManyRequestsPage />} />
                        <Route exact path="server-error" element={<ServerErrorPage />} />
                        <Route
                            exact
                            path="service-unavailable"
                            element={<ServiceUnavailablePage />}
                        />
                    </Route>
                    <Route path="/error" element={<ErrorLayout />}>
                        <Route exact path="401" element={<UnauthorizedPage />} />
                        <Route exact path="403" element={<ForbiddenPage />} />
                        <Route exact path="404" element={<NotFoundPage />} />
                        <Route exact path="419" element={<PageExpiredPage />} />
                        <Route exact path="429" element={<TooManyRequestsPage />} />
                        <Route exact path="500" element={<ServerErrorPage />} />
                        <Route exact path="503" element={<ServiceUnavailablePage />} />
                    </Route>
                    <Route path="*" element={<ErrorLayout />}>
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </MainContext.Provider>
    );
};
